import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';

import { Pill } from 'ui-components/components/pill';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import { useIsDocumentUploadEnabled } from '../../../../../feature-flags/hooks/use-is-document-upload-enabled';
import Global from '../../../../../store/global-selectors';
import { messages } from './messages';
import style from './carrier-document-warning-badge.module.less';

type CarrierDocumentWarningBadgeProps = {
  fulfillmentPlanId: string;
};

/**
 * Warning badge shown in the Customs Declarations header when the carrier
 * does not support electronic document upload.
 * Renders "1 Warning" with a tooltip on hover.
 */
export const CarrierDocumentWarningBadge = ({ fulfillmentPlanId }: CarrierDocumentWarningBadgeProps) => {
  const intl = useIntl();
  const isDocumentUploadEnabled = useIsDocumentUploadEnabled();

  const serviceId = useSelector((state) =>
    fulfillmentPlanId ? Global.selectEntitiesFulfillmentPlanServiceId(state, { fulfillmentPlanId }) : null,
  );
  const carrierApiCode = useSelector((state) => {
    if (!serviceId) return null;
    const service = Global.selectSellerServiceById(state, { serviceId });
    return service?.carrierApiCode ?? null;
  });
  const isLoadingCapabilities = useSelector((state) => Global.selectCustomsDocumentsIsLoadingCapabilities(state));
  const isCarrierSupported = useSelector((state) =>
    carrierApiCode ? Global.selectCustomsDocumentsCarrierSupportsDocumentUpload(state, carrierApiCode) : true,
  );

  if (!isDocumentUploadEnabled || isLoadingCapabilities || isCarrierSupported) {
    return null;
  }

  return (
    <Tooltip position='bottom'>
      <TooltipContent>{intl.formatMessage(messages.carrierDocumentUploadNotSupported)}</TooltipContent>
      <Pill
        icon={<FontAwesomeIcon className={style['warning-icon']} icon={faTriangleExclamation} />}
        label={intl.formatMessage(messages.warningBadgeLabel)}
        color='var(--yellow-200)'
        className={style['warning-pill']}
        textClassName={style['warning-text']}
      />
    </Tooltip>
  );
};
